import React from "react";
import { Switch, Tooltip } from "@merge-api/merge-javascript-shared";
import { ScopeOption } from "../../../../../../models/CommonModel";
import { SCOPE_LABEL, SCOPE_OPTIONAL_EXPLANATION } from "../constants";

interface Props {
  scope: ScopeOption;
  isViewing: boolean;
  areOptionalScopesEnabled: boolean;
  disabled?: boolean;
  /** Explanation shown on hover when the switch is disabled (permissions, plan, sub-object). May
   *  include a link (e.g. the upgrade "Get in touch" CTA), hence ReactNode. */
  disabledTooltip?: React.ReactNode;
  onChange: (scope: ScopeOption) => void;
}

/**
 * Enabled / Disabled / Optional control built on the shared merge-js Switch. View mode renders the
 * Switch's collapsed state (a single read-only pill); edit mode renders the full switch, greyed out
 * via `disabled` when it can't be changed (e.g. a sub-field whose sub-object is disabled). Optional
 * is offered only when end-user-configurable scopes are enabled.
 */
const ScopePermissionSwitch = ({
  scope,
  isViewing,
  areOptionalScopesEnabled,
  disabled,
  disabledTooltip,
  onChange,
}: Props) => {
  // derived state
  // `status` renders the colored dot on the switch (enabled → green, disabled → gray,
  // optional → orange). The enum values already match the Switch's status strings.
  const options = [
    {
      value: ScopeOption.ENABLED,
      label: SCOPE_LABEL[ScopeOption.ENABLED],
      status: "enabled" as const,
    },
    {
      value: ScopeOption.DISABLED,
      label: SCOPE_LABEL[ScopeOption.DISABLED],
      status: "disabled" as const,
    },
    ...(areOptionalScopesEnabled
      ? [
          {
            value: ScopeOption.OPTIONAL,
            status: "optional" as const,
            // Hovering the Optional label explains what it means. The Switch renders `label` as
            // node children, so a Tooltip-wrapped label works (the type says string, hence the
            // cast). Omitted when disabled so it doesn't nest inside the disabled-reason tooltip.
            label: (disabled ? (
              SCOPE_LABEL[ScopeOption.OPTIONAL]
            ) : (
              <Tooltip title={SCOPE_OPTIONAL_EXPLANATION}>
                <span>{SCOPE_LABEL[ScopeOption.OPTIONAL]}</span>
              </Tooltip>
            )) as unknown as string,
          },
        ]
      : []),
  ];

  // render
  // No manual opacity here — the shared Switch already fades itself to 40% when disabled.
  const control = (
    // Stop clicks bubbling to a clickable ancestor: the object row is an accordion that toggles
    // expand on click, so without this, clicking the switch also expands/collapses the row.
    <span className="inline-flex" onClick={(e) => e.stopPropagation()}>
      <Switch
        size="sm"
        collapsed={isViewing}
        disabled={disabled}
        options={options}
        value={scope}
        onChange={(value) => onChange(value as ScopeOption)}
      />
    </span>
  );

  // A disabled switch swallows hover events, so wrap it in a span for the Tooltip to anchor to.
  // leaveDelay keeps the tooltip open long enough to move into it and click any link (e.g. the
  // upgrade "Get in touch" CTA in the plan-gated tooltip).
  if (disabled && disabledTooltip) {
    return (
      <Tooltip title={disabledTooltip} leaveDelay={150}>
        <span className="inline-flex">{control}</span>
      </Tooltip>
    );
  }

  return control;
};

export default ScopePermissionSwitch;
